import { Entry } from 'contentful';
import * as React from 'react';
import { ContentfulFilters } from 'src/api/contentful/fetchEntries';
import { ApplicationError } from 'src/components/ApplicationError/ApplicaitonError';
import { BusyLoader } from 'src/components/BusyLoader/BusyLoader';
import {
  ContentfulEntry,
  ContentfulEntryRenderProps
} from 'src/components/EntryLoader/ContentfulEntry';
import { NotFoundPage } from 'src/components/ErrorPage/NotFoundPage';

/**
 * Enforces the render/children function type
 */
export type RenderPropFunc<EntryType extends object = any> = (entry: Entry<EntryType>) => JSX.Element | null | Array<JSX.Element | null>;

export type AutoContentfulEntryProvidedProps<EntryType extends object> = ContentfulFilters<EntryType> & {
  entryId?: string;

  disableLoader?: boolean;
  disableNotFound?: boolean;
  disableHasError?: boolean;

  // Allow defining the render props by either a prop or as children
  render?: RenderPropFunc<EntryType>;
  children?: RenderPropFunc<EntryType>;
};

/**
 * Applies the BusyLoader, ApplicationError, or NotFoundPage based on the isLoading, hasError, or notFound flags automatically.
 */
function AutoContentfulEntry<EntryType extends object = any>(props: AutoContentfulEntryProvidedProps<EntryType>) {
  const { children, render, disableLoader, disableNotFound, disableHasError, ...remainingProps } = props;

  if (!render && !children) {
    console.error('No render props were provided to ContentfulEntry.');
    return null;
  } else if (!!render && !!children) {
    console.error('You may provide either a render function as a prop OR as a child to ContentfulEntry but not both.');
    return null;
  }

  const renderFunc: RenderPropFunc<EntryType> = children || render!;

  return (
    <ContentfulEntry {...remainingProps}>
      {
        ({ isLoading, hasError, notFound, entry }: ContentfulEntryRenderProps<EntryType>) => {
          if (isLoading) return disableLoader ? null : <BusyLoader />;

          if (hasError) return disableHasError ? null : <ApplicationError />;

          if (notFound || !entry) return disableNotFound ? null : <NotFoundPage />;

          return renderFunc(entry);
        }}
    </ContentfulEntry>
  );
}

export {
  AutoContentfulEntry
};
